import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageSquare, X, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function generateId() {
  return "s-" + Math.random().toString(36).slice(2, 10) + "-" + Date.now().toString(36);
}

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(() => {
    const k = "hra_session_id";
    let v = localStorage.getItem(k);
    if (!v) {
      v = generateId();
      localStorage.setItem(k, v);
    }
    return v;
  });
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "THOCCY online. I'm the AI consultant for High-Risk Algorithms. Tell me which platform, score, or system is moving against you — I'll point to the right service.",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading, open]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat`, { session_id: sessionId, message: text });
      setMessages((m) => [...m, { role: "assistant", content: res.data.reply }]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Channel disrupted. Please retry in a moment, or file a brief on the Contact form for a human operator.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Launcher */}
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="chatbot-launcher"
        aria-label="Open chat"
        className="fixed bottom-24 right-6 z-40 inline-flex items-center gap-3 bg-red-600 hover:bg-red-500 px-5 py-4 font-mono text-[11px] uppercase tracking-[0.24em] text-white transition-colors shadow-[0_8px_40px_-8px_rgba(224,36,36,0.6)]"
      >
        {open ? <X size={16} /> : <MessageSquare size={16} />}
        {open ? "Close" : "Talk to Thoccy"}
      </button>

      {open && (
        <div
          data-testid="chatbot-panel"
          className="fixed bottom-44 right-4 sm:right-6 z-40 w-[calc(100vw-2rem)] sm:w-[420px] h-[560px] bg-[#0a0a0a] border border-white/10 flex flex-col shadow-2xl"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 bg-black">
            <div className="flex items-center gap-3">
              <span className="relative inline-flex h-2 w-2">
                <span className="absolute inset-0 bg-green-500 pulse-dot" />
                <span className="absolute inset-0 bg-green-500" />
              </span>
              <div>
                <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-white">
                  Thoccy
                </div>
                <div className="font-mono text-[9px] uppercase tracking-[0.24em] text-gray-500">
                  AI Consultant · Claude Sonnet 4.5
                </div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="chatbot-close"
              aria-label="Close chat"
              className="text-gray-400 hover:text-white"
            >
              <X size={16} />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chat-msg-${m.role}-${i}`}
                className={m.role === "user" ? "flex justify-end" : "flex justify-start"}
              >
                <div
                  className={`max-w-[85%] px-4 py-3 font-body text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-red-600 text-white"
                      : "bg-[#141414] border border-white/10 text-gray-200"
                  }`}
                >
                  {m.role === "assistant" && (
                    <div className="font-mono text-[9px] uppercase tracking-[0.28em] text-red-400 mb-2">
                      Thoccy
                    </div>
                  )}
                  <div className="whitespace-pre-wrap">{m.content}</div>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start" data-testid="chat-typing">
                <div className="bg-[#141414] border border-white/10 px-4 py-3 font-mono text-[11px] uppercase tracking-[0.24em] text-gray-400">
                  Thoccy is analyzing<span className="blink">_</span>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <form onSubmit={send} className="border-t border-white/10 p-4 flex items-center gap-3 bg-black">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about audits, suppression, counter-strategy…"
              data-testid="chatbot-input"
              className="flex-1 bg-transparent border-b border-white/20 focus:border-red-500 outline-none px-0 py-2 text-sm text-white placeholder-gray-600 font-body transition-colors"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              data-testid="chatbot-send"
              className="inline-flex items-center justify-center w-10 h-10 bg-red-600 hover:bg-red-500 text-white transition-colors disabled:opacity-40"
              aria-label="Send"
            >
              <Send size={14} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
